<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { ONBOARDING_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import OnboardingLayout from './shared/OnboardingLayout.vue';
import OnboardingSection from './shared/OnboardingSection.vue';
import InboxChannelsDialog from './inbox-setup/InboxChannelsDialog.vue';
import InboxChannelsFooter from './inbox-setup/InboxChannelsFooter.vue';
import ChannelRow from './inbox-setup/ChannelRow.vue';
import WebWidgetCreationStatus from './inbox-setup/WebWidgetCreationStatus.vue';
import HelpCenterCreationStatus from './inbox-setup/HelpCenterCreationStatus.vue';
import { CHANNEL_TYPES } from 'dashboard/helper/inbox';
import { useChannelConnect } from './inbox-setup/useChannelConnect';
import { useDetectedChannels } from './inbox-setup/useDetectedChannels';
import { DIALOG_CHANNELS } from './inbox-setup/constants';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { accountId, currentAccount, finishOnboarding } = useAccount();
const { isEnterprise } = useConfig();
const { connectViaOAuth, connectWhatsapp } = useChannelConnect();

const helpCenterGenerationId = computed(
  () => currentAccount.value?.custom_attributes?.help_center_generation_id
);

const isSubmitting = ref(false);

const inboxes = useMapGetter('inboxes/getInboxes');

const readinessChecks = computed(() => [
  {
    key: 'channel',
    complete: inboxes.value.length > 0,
    title: t('ONBOARDING_INBOX_SETUP.READINESS.CHECKS.CHANNEL.TITLE'),
    description:
      inboxes.value.length > 0
        ? t('ONBOARDING_INBOX_SETUP.READINESS.CHECKS.CHANNEL.COMPLETE')
        : t('ONBOARDING_INBOX_SETUP.READINESS.CHECKS.CHANNEL.INCOMPLETE'),
  },
  {
    key: 'routing',
    complete: inboxes.value.some(inbox => inbox.default_team_id),
    title: t('ONBOARDING_INBOX_SETUP.READINESS.CHECKS.ROUTING.TITLE'),
    description: inboxes.value.some(inbox => inbox.default_team_id)
      ? t('ONBOARDING_INBOX_SETUP.READINESS.CHECKS.ROUTING.COMPLETE')
      : t('ONBOARDING_INBOX_SETUP.READINESS.CHECKS.ROUTING.INCOMPLETE'),
  },
  {
    key: 'knowledge',
    complete: inboxes.value.some(
      inbox => inbox.captain_assistant || inbox.help_center
    ),
    title: t('ONBOARDING_INBOX_SETUP.READINESS.CHECKS.KNOWLEDGE.TITLE'),
    description: inboxes.value.some(
      inbox => inbox.captain_assistant || inbox.help_center
    )
      ? t('ONBOARDING_INBOX_SETUP.READINESS.CHECKS.KNOWLEDGE.COMPLETE')
      : t('ONBOARDING_INBOX_SETUP.READINESS.CHECKS.KNOWLEDGE.INCOMPLETE'),
  },
]);

const completedReadinessChecks = computed(
  () => readinessChecks.value.filter(check => check.complete).length
);

const {
  displayedChannels,
  remainingChannels,
  connectedInbox,
  hasDetectedChannels,
} = useDetectedChannels();

const channelsDialogRef = ref(null);

// The initial inboxes fetch happens in WebWidgetCreationStatus, which polls
// `inboxes/get` from its own mount — no need to dispatch it here too.
onMounted(() => {
  useTrack(ONBOARDING_EVENTS.INBOX_SETUP_VISITED);
});

const completeOnboarding = async event => {
  if (isSubmitting.value) return;

  isSubmitting.value = true;
  try {
    // Declare the step we're completing so the controller only clears it when
    // the stored step still matches (idempotent). setUser then refreshes the
    // auth store so the router guard sees the cleared step and lets us in.
    await finishOnboarding({ onboarding_step: 'inbox_setup' });
    useTrack(event);
    await store.dispatch('setUser');
    router.push({ name: 'home', params: { accountId: accountId.value } });
  } catch {
    useAlert(t('ONBOARDING_INBOX_SETUP.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const handleContinue = () =>
  completeOnboarding(ONBOARDING_EVENTS.INBOX_SETUP_COMPLETED);
const handleSkip = () =>
  completeOnboarding(ONBOARDING_EVENTS.INBOX_SETUP_SKIPPED);
const openChannelsDialog = () => channelsDialogRef.value?.open();
const refetchInboxes = () => store.dispatch('inboxes/get');

// WhatsApp connects via Meta's embedded-signup popup; the DIALOG_CHANNELS open
// the channels dialog preselected to their in-dialog step; the rest go through
// the redirect OAuth flow (Gmail/Outlook keyed by email provider, Instagram by
// channel type).
const connectChannel = channel => {
  if (channel.type === CHANNEL_TYPES.WHATSAPP) {
    connectWhatsapp();
    return;
  }
  if (DIALOG_CHANNELS.includes(channel.type)) {
    channelsDialogRef.value?.open(channel.type);
    return;
  }
  connectViaOAuth(channel.inbox?.provider || channel.type);
};
</script>

<template>
  <div>
    <OnboardingLayout
      :greeting="t('ONBOARDING_INBOX_SETUP.GREETING')"
      :subtitle="t('ONBOARDING_INBOX_SETUP.SUBTITLE')"
      :continue-label="t('ONBOARDING_INBOX_SETUP.CONTINUE')"
      :skip-label="t('ONBOARDING_INBOX_SETUP.SKIP')"
      :is-loading="isSubmitting"
      @continue="handleContinue"
      @skip="handleSkip"
    >
      <template #greeting-icon>
        <Icon icon="i-lucide-wrench" class="size-4 text-n-slate-7" />
      </template>

      <OnboardingSection
        :title="t('ONBOARDING_INBOX_SETUP.CREATED_FOR_YOU.TITLE')"
        icon="i-lucide-sparkles"
      >
        <div class="divide-y divide-n-weak">
          <WebWidgetCreationStatus />
          <HelpCenterCreationStatus
            v-if="isEnterprise && helpCenterGenerationId"
          />
        </div>
      </OnboardingSection>

      <OnboardingSection
        :title="t('ONBOARDING_INBOX_SETUP.CHANNELS.TITLE')"
        icon="i-lucide-inbox"
      >
        <div
          v-if="hasDetectedChannels"
          class="flex items-center gap-2 p-3 border-b border-dashed border-n-strong"
        >
          <Icon
            icon="i-lucide-lightbulb"
            class="size-4 text-n-slate-11 flex-shrink-0"
          />
          <span class="flex-1 min-w-0 text-body-main text-n-slate-11">
            {{ t('ONBOARDING_INBOX_SETUP.CHANNELS.HEADER') }}
          </span>
        </div>
        <ChannelRow
          v-for="channel in displayedChannels"
          :key="channel.type"
          :channel="channel"
          :connected-inbox="connectedInbox(channel)"
          @connect="connectChannel"
        />
        <InboxChannelsFooter
          :remaining-channels="remainingChannels"
          @view-all="openChannelsDialog"
        />
      </OnboardingSection>

      <OnboardingSection
        :title="t('ONBOARDING_INBOX_SETUP.READINESS.TITLE')"
        icon="i-lucide-list-checks"
      >
        <div
          class="flex items-center justify-between gap-3 border-b border-n-weak px-3 py-2.5"
        >
          <span class="text-sm text-n-slate-11">
            {{ t('ONBOARDING_INBOX_SETUP.READINESS.DESCRIPTION') }}
          </span>
          <span class="shrink-0 text-xs font-medium text-n-slate-10">
            {{
              t('ONBOARDING_INBOX_SETUP.READINESS.PROGRESS', {
                complete: completedReadinessChecks,
                total: readinessChecks.length,
              })
            }}
          </span>
        </div>
        <div class="divide-y divide-n-weak">
          <div
            v-for="check in readinessChecks"
            :key="check.key"
            class="flex items-start gap-3 px-3 py-3"
          >
            <Icon
              :icon="
                check.complete
                  ? 'i-lucide-circle-check'
                  : 'i-lucide-circle-dashed'
              "
              class="mt-0.5 size-4 shrink-0"
              :class="check.complete ? 'text-n-teal-10' : 'text-n-slate-9'"
            />
            <div class="min-w-0">
              <p class="mb-0 text-sm font-medium text-n-slate-12">
                {{ check.title }}
              </p>
              <p class="mb-0 mt-0.5 text-xs text-n-slate-10">
                {{ check.description }}
              </p>
            </div>
          </div>
        </div>
      </OnboardingSection>
    </OnboardingLayout>
    <InboxChannelsDialog
      ref="channelsDialogRef"
      :inboxes="inboxes"
      @connected="refetchInboxes"
    />
  </div>
</template>
